import React from 'react';
import { Apple, Play, Star, ArrowRight } from 'lucide-react';
import { skylineImage, heroImage } from '../data/mock';

const Hero = () => {
  return (
    <section id="home" className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-gradient-to-b from-[#0b2f5c] via-[#0d3567] to-[#0b2f5c]">
      {/* Background skyline */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${skylineImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b2f5c]/70 via-[#0b2f5c]/60 to-[#0b2f5c]" />

      {/* Decorative glow */}
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-sky-500/15 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
              <span className="flex h-2 w-2">
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-sky-300 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-300" />
              </span>
              <span className="text-xs font-medium tracking-wide text-white/90">
                Proudly built in Botswana
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-6">
              Empowering{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-sky-300 to-sky-100 bg-clip-text text-transparent">
                  everyday life
                </span>
              </span>
              <br />
              in Botswana
            </h1>

            <p className="text-lg text-white/75 leading-relaxed mb-8 max-w-xl">
              MaLife connects you to rides, deliveries and essential services that make daily life easier, safer and smarter — one tap away.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <a
                href="#download"
                className="group inline-flex items-center gap-3 bg-white text-[#0b2f5c] px-5 py-3 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <Apple className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-[10px] leading-none text-slate-500">Download on the</div>
                  <div className="text-base font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a
                href="#download"
                className="group inline-flex items-center gap-3 bg-[#0b2f5c] border border-white/30 text-white px-5 py-3 rounded-2xl shadow-lg hover:bg-[#0e3874] hover:-translate-y-0.5 transition-all"
              >
                <Play className="w-6 h-6 fill-white" />
                <div className="text-left">
                  <div className="text-[10px] leading-none text-white/60">GET IT ON</div>
                  <div className="text-base font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-[#0b2f5c] bg-gradient-to-br from-sky-300 to-sky-500 flex items-center justify-center text-xs font-bold text-white"
                  >
                    {['K', 'N', 'T', 'M'][i - 1]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-sky-300 text-sky-300" />
                  ))}
                </div>
                <div className="text-sm text-white/70">
                  <span className="font-semibold text-white">4.9/5</span> from 2,400+ reviews
                </div>
              </div>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-8 bg-sky-400/30 blur-3xl rounded-full" />
              <div className="relative w-[300px] md:w-[360px] aspect-[9/19] bg-gradient-to-b from-[#0b2f5c] to-[#062348] rounded-[3rem] p-3 shadow-2xl border-4 border-slate-900">
                <div className="w-full h-full rounded-[2.4rem] bg-gradient-to-b from-sky-50 to-white overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl z-10" />

                  {/* App content */}
                  <div className="p-6 pt-10">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="text-xs text-slate-500">Dumela,</div>
                        <div className="text-lg font-bold text-[#0b2f5c]">Kagiso</div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-300 to-sky-500 flex items-center justify-center text-white font-bold shadow">
                        K
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#0b2f5c] to-[#164a8f] rounded-2xl p-4 text-white mb-5 shadow-lg">
                      <div className="text-xs text-sky-200 mb-1">Where to?</div>
                      <div className="text-sm font-medium mb-3">Airport Junction Mall</div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-sky-200">Estimated fare</span>
                        <span className="font-bold">P 45</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-5">
                      {['Ride', 'Deliver', 'Fix', 'Assist'].map((s, i) => (
                        <div key={s} className="text-center">
                          <div className="w-12 h-12 mx-auto rounded-2xl bg-sky-100 flex items-center justify-center mb-1 shadow-sm">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-sky-400 to-sky-600" />
                          </div>
                          <div className="text-[10px] font-medium text-slate-700">{s}</div>
                        </div>
                      ))}
                    </div>

                    <div className="text-xs font-semibold text-slate-600 mb-2">Recent trips</div>
                    <div className="space-y-2">
                      {['Main Mall → Home', 'Home → Sir Seretse'].map((t) => (
                        <div key={t} className="flex items-center justify-between bg-slate-50 rounded-xl p-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                              <ArrowRight className="w-4 h-4 text-sky-600" />
                            </div>
                            <div className="text-[11px] text-slate-700 font-medium">{t}</div>
                          </div>
                          <div className="text-[10px] text-slate-400">2d ago</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating card */}
              <div className="absolute -left-16 lg:-left-24 top-32 bg-white rounded-2xl shadow-2xl p-4 w-52 hidden md:block animate-[float_6s_ease-in-out_infinite] z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-sky-400 to-sky-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-800">Driver arriving</div>
                    <div className="text-[10px] text-slate-500">Tebogo · Toyota Vitz</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] text-slate-500">ETA</div>
                  <div className="text-xs font-bold text-sky-600">3 min</div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -right-6 bottom-16 bg-white rounded-2xl shadow-2xl p-3 hidden md:flex items-center gap-2 animate-[float_5s_ease-in-out_infinite_reverse]">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 leading-none">Live</div>
                  <div className="text-xs font-bold text-slate-800 leading-tight">Trip started</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" className="w-full h-12 md:h-20" preserveAspectRatio="none">
          <path d="M0,40 C240,80 480,0 720,20 C960,40 1200,80 1440,40 L1440,80 L0,80 Z" fill="#f8fafc" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
